import { useMemo, useCallback, useContext, useEffect } from 'react'

import { Box, hexToRgb } from '@mui/material'
import { EditorContent, useEditor } from '@tiptap/react'
import debounce from 'lodash/debounce'
import type { UpdatePageInput } from 'types/graphql'

import { useQuery } from '@redwoodjs/web'

import { compactEditorStyle } from 'src/components/Drafts/utils'
import PromptInput from 'src/components/Prompts/PromptInput'
import Row from 'src/components/Row/Row'
import SidebarLoader from 'src/components/Sidebar/SidebarLoader/SidebarLoader'
import AiEditor from 'src/components/Tiptap/AiEditor/AiEditor'
import { extensionsForParentObject } from 'src/components/Tiptap/extensions'
import { editorStyle } from 'src/components/Tiptap/styles'
import TiptapBubbleMenu from 'src/components/Tiptap/TiptapBubbleMenu/TiptapBubbleMenu'
import TiptapFloatingMenu from 'src/components/Tiptap/TiptapFloatingMenu/TiptapFloatingMenu'
import { usePage } from 'src/hooks/usePage/usePage'
import { DayContext } from 'src/lib/dayContext'
import { logger } from 'src/lib/logger'
import { NativeObjectTypes } from 'src/lib/objects'
import type { DocumentObject } from 'src/lib/pages'
import { GET_PAGE_INSTRUCTIONS } from 'src/pages/Import/ImportPage/queries'

import { extractTitleAndContent } from '../pages'

// Sanitize content JSON to fix malformed text nodes
const sanitizeContentJson = (content: any): any => {
  if (!content || typeof content !== 'object') return content

  if (Array.isArray(content)) {
    return content.map(sanitizeContentJson).filter(Boolean)
  }

  const sanitized = { ...content }

  // Fix malformed text nodes (type: "text" but missing text property)
  if (sanitized.type === 'text' && !sanitized.text) {
    return null // Remove invalid text nodes
  }

  // Fix paragraph nodes without content property
  if (sanitized.type === 'paragraph' && !sanitized.content) {
    sanitized.content = [] // Empty paragraphs should have empty content array
  }

  // Fix title nodes with empty or malformed content
  if (sanitized.type === 'title') {
    if (!sanitized.content || !Array.isArray(sanitized.content)) {
      sanitized.content = [{ type: 'text', text: '' }]
    } else {
      const validContent = sanitized.content
        .map(sanitizeContentJson)
        .filter(Boolean)
      // If title has no valid content, add a default text node
      if (validContent.length === 0) {
        sanitized.content = [{ type: 'text', text: '' }]
      } else {
        sanitized.content = validContent
      }
    }
  }
  // Recursively sanitize content arrays for other node types
  else if (sanitized.content && Array.isArray(sanitized.content)) {
    sanitized.content = sanitized.content
      .map(sanitizeContentJson)
      .filter(Boolean)
  }

  return sanitized
}

const containerSx = ({
  compact,
  showPrompt,
}: {
  compact: boolean
  showPrompt: boolean
}) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  '& .editor-container': {
    height: '100%',
    width: '100%',
    flex: 1,
    display: 'flex',
    overflow: 'hidden',
    '& .editor-tiptap': {
      ...(compact ? compactEditorStyle : editorStyle),
      width: '100%',
      '& > div, .editor-tiptap-inner': {
        height: '100%',
        width: '100%',
      },
      '& .tiptap.ProseMirror': {
        overflowY: 'auto',
        height: '100%',
      },
    },
    '& .editor-prompt': {
      display: showPrompt ? 'flex' : 'none',
      flexShrink: 0,
      width: '100%',
      background: (theme) => theme.palette.background.default,
      position: 'relative',
      pt: 2,
      '&::before': {
        content: '""',
        position: 'absolute',
        top: '-8px',
        left: 0,
        right: 0,
        height: '8px',
        background: (theme) =>
          `linear-gradient(to top, ${hexToRgb(
            theme.palette.background.default
          )}, rgba(0, 0, 0, 0))`,
        zIndex: 9,
      },
    },
  },
})

const EmbeddedAiPageEditor = ({
  userHasEditorAccess,
  parentObject,
}: {
  userHasEditorAccess: boolean
  parentObject: DocumentObject
}) => {
  const {
    page,
    contextObjects,
    token,
    handleUpdatePageContent,
    contextString,
    contextStatus,
  } = usePage()

  const showPrompt = false

  const { selectedWorkspace } = useContext(DayContext)

  const aiUnderway = page?.aiInitialPrompt && !page?.aiPopulationCompletedAt

  const { data: pageInstructions } = useQuery(GET_PAGE_INSTRUCTIONS, {
    variables: {
      workspaceId: selectedWorkspace,
      parentObject: parentObject,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    skip: !selectedWorkspace || !aiUnderway,
  })

  const instructions = useMemo(() => {
    return pageInstructions?.getPageInstructions || ''
  }, [pageInstructions])

  const handleUpdatePageContentEditor = useCallback(
    async (updatePayload) => {
      const editor = updatePayload.editor
      const currentContent = editor.getJSON()
      const contentHtml = editor.getHTML()

      const { title: titleNode, content } =
        extractTitleAndContent(currentContent)
      const title = titleNode?.content[0]?.text || undefined

      const input: {
        contentJson: { type: string; content: any }
        contentHtml: any
        title?: string
      } = {
        contentJson: content,
        contentHtml,
        title,
      }

      try {
        logger.dev('Updating page content', { input })
        await handleUpdatePageContent({
          ...input,
          workspaceId: page?.workspaceId,
        })
      } catch (error) {
        logger.error('Failed to update page content:', error)
      }
    },
    [handleUpdatePageContent, page?.workspaceId]
  )

  const handleSendPrompt = (state: any, dispatch: any) => {
    dispatch({
      type: 'EDIT_PROMPT',
      payload: '',
    })
  }

  const handleAiResponse = useCallback(
    (response) => {
      const responseEditor = response.editor
      const contentJson = responseEditor.getJSON()
      const contentHtml = responseEditor.getHTML()
      logger.dev('handleAiResponse', {
        responseEditor,
        contentJson,
        contentHtml,
      })
      const input: UpdatePageInput = {
        contentJson,
        contentHtml,
        aiPopulationCompletedAt: new Date().toISOString(),
        workspaceId: page?.workspaceId,
      }
      handleUpdatePageContent(input)
    },
    [handleUpdatePageContent, page?.workspaceId]
  )

  const debouncedUpdateContent = useMemo(() => {
    const debouncedFn = debounce((editor) => {
      logger.dev('debouncedUpdateContent called')
      handleUpdatePageContentEditor(editor)
    }, 750)
    return debouncedFn
  }, [handleUpdatePageContentEditor])

  return (
    <Box
      sx={containerSx({
        compact: parentObject.objectType === NativeObjectTypes.Draft,
        showPrompt,
      })}
    >
      {!page || (!instructions && aiUnderway) ? (
        <SidebarLoader />
      ) : (
        <>
          <Box className="editor-container">
            <>
              <Box className="editor-tiptap">
                {aiUnderway ? (
                  <AiEditor
                    token={token}
                    prompt={page?.aiInitialPrompt}
                    onSuccess={handleAiResponse}
                    contextString={contextString}
                    parentObject={parentObject}
                    instructions={instructions}
                  />
                ) : (
                  <PageEditor
                    onUpdate={debouncedUpdateContent}
                    userHasEditorAccess={userHasEditorAccess}
                    parentObject={parentObject}
                  />
                )}
              </Box>
            </>
          </Box>
          {showPrompt && (
            <Box className="editor-prompt">
              <Row sx={{ width: '100%' }}>
                <Box sx={{ width: '100%', mx: 2 }}>
                  <PromptInput
                    placeholder="Ask Day.ai to revise the page"
                    contextObjects={contextObjects}
                    onSubmit={handleSendPrompt}
                    workspaceId={page?.workspaceId}
                    contextStatus={contextStatus}
                    showStatus={true}
                    showDebug={true}
                    debugComponent="page"
                  />
                </Box>
              </Row>
            </Box>
          )}
        </>
      )}
    </Box>
  )
}

const PageEditor = ({ onUpdate, userHasEditorAccess, parentObject }) => {
  const { page } = usePage()

  const extensions = useMemo(() => {
    return [
      ...extensionsForParentObject({
        parentObject,
      }),
    ]
  }, [parentObject])

  const editor = useEditor({
    extensions,
    autofocus: true,
    editable: userHasEditorAccess,
    onUpdate,
  })

  useEffect(() => {
    if (!editor) return

    logger.dev('EmbeddedAiPageEditor content initialization debug:', {
      pageId: page?.id,
      pageTitle: page?.title,
      hasContentJson: !!page?.contentJson,
      contentJsonType: typeof page?.contentJson,
      contentJsonKeys: page?.contentJson ? Object.keys(page?.contentJson) : [],
      hasContentHtml: !!page?.contentHtml,
      contentHtmlLength: page?.contentHtml?.length,
    })

    // Process contentJson with proper fallbacks
    let contentToSet = null

    // Check if contentJson is corrupted (empty object) but contentHtml exists
    const isContentJsonCorrupted =
      page?.contentJson &&
      typeof page.contentJson === 'object' &&
      Object.keys(page.contentJson).length === 0 &&
      page?.contentHtml

    if (isContentJsonCorrupted) {
      // Use HTML fallback - TipTap will parse it properly
      contentToSet = page.contentHtml
      logger.dev(
        'EmbeddedAiPageEditor: Using HTML due to corrupted contentJson',
        {
          pageId: page?.id,
          pageTitle: page?.title,
        }
      )
    } else if (page?.contentJson) {
      // Handle both proper Tiptap document format and array-like objects
      const contentToProcess = (page?.contentJson as any)?.content
        ? page?.contentJson // Already in document format
        : { type: 'doc', content: Object.values(page?.contentJson) } // Convert array-like object

      logger.dev('EmbeddedAiPageEditor: Processing content:', {
        originalContentJson: page?.contentJson,
        contentToProcess,
      })

      const { content } = extractTitleAndContent(contentToProcess)
      logger.dev('EmbeddedAiPageEditor: Extracted content:', { content })

      // If no actual content (just title), fallback to HTML
      if (content?.content?.length === 0 && page?.contentHtml) {
        contentToSet = page.contentHtml
        logger.dev('EmbeddedAiPageEditor: No content, falling back to HTML')
      } else {
        contentToSet = content
      }
    } else if (page?.contentHtml) {
      // Final fallback to HTML
      contentToSet = page.contentHtml
      logger.dev('EmbeddedAiPageEditor: Using contentHtml fallback')
    }

    // Sanitize content before setting to prevent DOM nodeType errors
    if (contentToSet) {
      try {
        const sanitizedContent =
          typeof contentToSet === 'string'
            ? contentToSet
            : sanitizeContentJson(contentToSet)
        editor.commands.setContent(sanitizedContent)
      } catch (setContentError) {
        logger.error('Failed to set editor content:', setContentError)
        // Fallback: set empty content to prevent crashes
        try {
          editor.commands.setContent('')
        } catch (fallbackError) {
          logger.error('Even fallback setContent failed:', fallbackError)
        }
      }
    } else {
      logger.dev('EmbeddedAiPageEditor: No content available, leaving empty')
    }
  }, [editor, page?.contentJson, page?.contentHtml, page?.id])

  return (
    <div className="editor-tiptap-inner">
      <TiptapBubbleMenu
        editor={editor}
        includedOptions={extensions.map((extension) => extension.name)}
      />
      {parentObject.objectType === NativeObjectTypes.Page && (
        <TiptapFloatingMenu editor={editor} />
      )}
      <EditorContent editor={editor} />
    </div>
  )
}

export default EmbeddedAiPageEditor
